import { visibleControlPoint, DRAWING_ANCHOR_OPTIONS } from "./controlAnchorPoint";
import { useId, useLayoutEffect, type RefObject } from "react";
import type BaseObject from "../models/BaseObject";
import type { ConnectionAnalyser } from "./ConnectionAnalyser";
import type {
  ConnectionEndpoint,
  ConnectionEndpointProvider,
  ConnectionEndpointSurface,
} from "./ConnectionEndpointProvider";
import { connectionOwners } from "./collectSelectedConnections";
import { centerDrawingAnchor } from "./centerDrawingAnchor";
import { connectableControlEndpoint } from "./connectableControlEndpoint";
import { measureEndpointContour } from "./endpointContour";
import { controlExitDirection } from "./controlExitDirection";

/** Registers this control, including represented device channels, for its mounted lifetime. */
export function useConnectionEndpoint(
  analyser: ConnectionAnalyser,
  object: BaseObject | undefined,
  element: RefObject<Element | null>,
  surface: ConnectionEndpointSurface,
  enabled = true,
  anchorSelector?: string,
): void {
  const id = useId();
  useLayoutEffect(() => {
    if (!enabled || !object) return;
    const provider: ConnectionEndpointProvider = {
      id,
      getConnectableEndpoints() {
        const control = anchorSelector ? element.current?.querySelector(anchorSelector) : element.current;
        const endpoint = connectableControlEndpoint(object, control ?? null, surface);
        if (endpoint) endpoint.hoverElement = element.current ?? undefined;
        return endpoint ? [endpoint] : [];
      },
      bringIntoView(objectId, _targetObjectId, options) {
        if (surface !== "drawing" || !element.current) return;
        const channel = element.current.hasAttribute("data-connection-channel-anchors")
          ? element.current.querySelector(`[data-connection-object-id="${CSS.escape(objectId)}"]`) : null;
        const anchor = channel ?? (anchorSelector ? element.current.querySelector(anchorSelector) : element.current);
        if (anchor) centerDrawingAnchor(anchor, !options?.center);
      },
      getConnectionEndpoint(connection) {
        const owners = connectionOwners(object);
        const start = owners.some((owner) => owner.id === connection.fromId);
        const end = owners.some((owner) => owner.id === connection.toId);
        if ((!start && !end) || !element.current) return [];
        // Design buttons expose the same key rectangles as the editor; technical tags use the device anchor.
        const channelId = start ? connection.fromId : connection.toId;
        const channelElement = channelId && surface === "drawing" && element.current.hasAttribute("data-connection-channel-anchors")
          ? element.current.querySelector(`[data-connection-object-id="${CSS.escape(channelId)}"]`) : null;
        const anchorElement = channelElement ?? (anchorSelector ? element.current.querySelector(anchorSelector) : element.current);
        if (!anchorElement) return [];
        const point = visibleControlPoint(anchorElement, surface === "drawing" ? DRAWING_ANCHOR_OPTIONS : undefined);
        if (!point) return [];
        const contour = measureEndpointContour(anchorElement, point, surface);
        // Plan keys keep their own outward side instead of choosing the edge facing the partner.
        const exitDirection = channelElement ? controlExitDirection(channelElement) : undefined;
        const result: ConnectionEndpoint[] = [];
        if (start)
          result.push({
            connectionId: connection.id,
            role: "start",
            point,
            contour,
            exitDirection,
            surface,
            representedObjectClass: object.className,
            anchorObjectId: channelElement ? channelId : object.id,
          });
        if (end)
          result.push({
            connectionId: connection.id,
            role: "end",
            point,
            contour,
            exitDirection,
            surface,
            representedObjectClass: object.className,
            anchorObjectId: channelElement ? channelId : object.id,
          });
        return result;
      },
    };
    analyser.registerEndpointProvider(provider);
    const resize = new ResizeObserver(() => analyser.refreshEndpoints());
    if (element.current) resize.observe(element.current);
    return () => {
      resize.disconnect();
      analyser.unregisterEndpointProvider(provider);
    };
  }, [analyser, object, element, surface, enabled, anchorSelector, id]);
}
